import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, BACKEND, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ssoEnabled, setSsoEnabled] = useState(false);

  React.useEffect(() => {
    if (user) nav(loc.state?.from?.pathname || "/", { replace: true });
  }, [user, nav, loc]);

  React.useEffect(() => {
    api.get("/auth/oidc/status").then((r) => setSsoEnabled(!!r.data?.enabled)).catch(() => {});
    const err = new URLSearchParams(loc.search).get("sso_error");
    if (err) {
      const map = {
        forbidden: "Bu hesabın panele erişim yetkisi yok. (Netlen ID grubunuzu kontrol edin.)",
        denied: "Netlen ID girişi iptal edildi.",
        noemail: "Netlen ID hesabında e-posta tanımlı değil.",
        state: "Oturum doğrulaması başarısız, tekrar deneyin.",
        token: "Netlen ID ile token alışverişi başarısız.",
        verify: "Netlen ID kimlik doğrulaması başarısız.",
      };
      toast.error(map[err] || "Netlen ID ile giriş başarısız.");
    }
  }, [loc.search]);

  const startSso = () => {
    window.location.href = `${BACKEND}/api/auth/oidc/login`;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Giriş başarılı");
      nav(loc.state?.from?.pathname || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-background noise-bg overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(74,114,246,0.12), transparent 60%), radial-gradient(ellipse 40% 30% at 80% 100%, rgba(74,114,246,0.08), transparent 60%)",
        }}
      />

      <div className="relative z-10 w-full max-w-md px-6">
        <div className="mb-8 flex flex-col items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_dns-orchestrator/artifacts/qxf4tj1j_netlen-white.svg"
            alt="Netlen"
            className="h-10 w-auto mb-3"
          />
          <div className="text-xs font-mono uppercase tracking-[0.35em] text-muted-foreground">
            DNS Orchestrator
          </div>
          <h1 className="mt-3 text-2xl font-semibold tracking-tight">Yönetim Paneli</h1>
        </div>

        <div className="border border-border bg-card rounded-md p-6 shadow-2xl shadow-black/40">
          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                E-posta
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="admin@netlen.com.tr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                Parola
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password"
              />
            </div>
            <Button type="submit" disabled={busy} className="w-full h-10" data-testid="login-submit">
              {busy ? <><Loader2 className="animate-spin mr-2" size={14} /> Doğrulanıyor...</> : "Giriş Yap"}
            </Button>
          </form>

          {ssoEnabled && (
            <>
              <div className="my-4 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                <div className="h-px flex-1 bg-border" /> veya <div className="h-px flex-1 bg-border" />
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full h-10"
                onClick={startSso}
                data-testid="login-sso"
              >
                Netlen ID ile Giriş
              </Button>
            </>
          )}
        </div>

        <div className="mt-6 text-center text-[11px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
          Netlen · DNS Orchestrator · v.1.1
        </div>
      </div>
    </div>
  );
}
